import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Layout } from "@/components/layout/Layout";
import SEO from "@/components/SEO.jsx";
import { SEO_KEYWORDS } from "@/seo/seoData";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  Globe,
  Code,
  Smartphone,
  Zap,
  Shield,
  TrendingUp,
  CheckCircle,
  ChevronDown,
  Layers,
} from "lucide-react";

const capabilities = [
  {
    icon: Shield,
    title: "Social Media Strategy",
    description:
      "Planning built around audience, goals, message, and the role social media should play for the business.",
  },
  {
    icon: Layers,
    title: "Content Direction",
    description:
      "Clear content themes, post ideas, and structured messaging that support consistency.",
  },
  {
    icon: Code,
    title: "Content Creation Support",
    description:
      "Support for branded post copy, creative direction, and campaign-ready content planning.",
  },
  {
    icon: TrendingUp,
    title: "Paid Social Advertising",
    description:
      "Targeted paid campaign support where social media needs stronger reach or faster traction.",
  },
  {
    icon: Smartphone,
    title: "Community Presence",
    description:
      "Support for building a more active and credible brand presence online.",
  },
  {
    icon: Zap,
    title: "Performance Review",
    description:
      "Content and campaign review based on what is working, what is missing, and what should improve.",
  },
];

const offerings = [
  {
    title: "Brand Content Strategy",
    description:
      "Content direction that helps your business show up more clearly and consistently.",
  },
  {
    title: "Platform-Specific Content Planning",
    description:
      "Content structured around the platforms your audience is most likely to use.",
  },
  {
    title: "Paid Campaign Support",
    description:
      "Paid social planning for offers, awareness, or targeted audience growth.",
  },
  {
    title: "Business-Focused Social Direction",
    description:
      "Social media support that works alongside your website, SEO, and broader digital presence.",
  },
];

const deliverables = [
  "Content direction based on business goals",
  "Brand messaging guidance for social posts",
  "Content themes and planning structure",
  "Paid social campaign input where needed",
  "Creative direction support",
  "Posting and visibility recommendations",
  "Content review and refinement",
  "Clear next-step priorities",
];

const processSteps = [
  {
    step: "01",
    title: "Discovery",
    description:
      "We learn about your business, audience, brand direction, and what social media needs to support.",
  },
  {
    step: "02",
    title: "Strategy",
    description:
      "We define the message, content focus, platform priorities, and overall direction.",
  },
  {
    step: "03",
    title: "Planning",
    description:
      "We organize themes, content types, and campaign opportunities around the business goals.",
  },
  {
    step: "04",
    title: "Content Direction",
    description:
      "We shape what should be posted, how it should sound, and how it should support trust and visibility.",
  },
  {
    step: "05",
    title: "Review",
    description:
      "We assess what is working, where the message is landing, and what should improve.",
  },
  {
    step: "06",
    title: "Refine & Support",
    description:
      "We continue improving direction, consistency, and campaign support over time.",
  },
];

const faqs = [
  {
    question: "What is included in your social media marketing service?",
    answer: (
      <>
        The service can include strategy, content direction, content planning,
        creative guidance, paid social support, and performance review
        depending on what the business needs.
      </>
    ),
    schemaAnswer:
      "The service can include strategy, content direction, content planning, creative guidance, paid social support, and performance review depending on what the business needs.",
  },
  {
    question: "Do you create the content too?",
    answer: (
      <>
        We can support content direction, messaging, and creative planning. The
        exact content creation scope depends on the project setup.
      </>
    ),
    schemaAnswer:
      "We can support content direction, messaging, and creative planning. The exact content creation scope depends on the project setup.",
  },
  {
    question: "Do you also manage paid ads?",
    answer: (
      <>
        We can support paid social advertising where it fits the business goal
        and message clearly.
      </>
    ),
    schemaAnswer:
      "We can support paid social advertising where it fits the business goal and message clearly.",
  },
  {
    question: "Which platforms do you work with?",
    answer: (
      <>
        That depends on where your audience is most active and what role social
        media should play in your wider{" "}
        <Link
          to="/services/seo-services"
          className="text-primary hover:underline"
        >
          digital strategy
        </Link>
        .
      </>
    ),
    schemaAnswer:
      "That depends on where your audience is most active and what role social media should play in your wider digital strategy.",
  },
  {
    question: "Will social media help if my website is weak?",
    answer: (
      <>
        Social media can improve visibility, but it works best when the{" "}
        <Link
          to="/services/website-development"
          className="text-primary hover:underline"
        >
          website and core digital presence
        </Link>{" "}
        also support conversion and trust.
      </>
    ),
    schemaAnswer:
      "Social media can improve visibility, but it works best when the website and core digital presence also support conversion and trust.",
  },
  {
    question: "How do you decide what content to post?",
    answer: (
      <>
        We look at the business goals, audience questions, content
        opportunities, and the kind of message that will support visibility and
        trust.
      </>
    ),
    schemaAnswer:
      "We look at the business goals, audience questions, content opportunities, and the kind of message that will support visibility and trust.",
  },
];

const whyChooseUs = [
  {
    title: "Strategy Before Content",
    description:
      "We define the audience, message, and goals before planning what gets posted.",
  },
  {
    title: "Built Around the Business",
    description:
      "The content direction is shaped around your business model, audience, and actual priorities.",
  },
  {
    title: "Organic + Paid Thinking",
    description:
      "We can support both consistent brand content and paid social campaigns where they make sense.",
  },
  {
    title: "Clearer Content Direction",
    description:
      "The goal is not more noise. The goal is stronger visibility with a clearer message.",
  },
];

export default function SocialMediaMarketingPage() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <Layout>
      <SEO
        title="Best Social Media Management Agency to build your Brand | Square Trigger"
        description="Get Social Media Marketing services, including paid advertising strategies, to be ahead of your competitors. Establish a strong, recognizable presence today."
        ogTitle="Best Social Media Management Agency to build your Brand | Square Trigger"
        ogDescription="Get Social Media Marketing services, including paid advertising strategies, to be ahead of your competitors. Establish a strong, recognizable presence today."
        keywords={SEO_KEYWORDS.socialMediaMarketing}
      />

      {/* Hero Section */}
      <section className="pt-24 sm:pt-28 lg:pt-32 pb-16 sm:pb-18 lg:pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-foreground via-foreground/95 to-foreground/90" />
        <div className="absolute inset-0">
          <div className="absolute top-16 sm:top-20 right-6 sm:right-20 w-72 sm:w-96 h-72 sm:h-96 bg-primary/20 rounded-full blur-3xl" />
          <div className="absolute bottom-16 sm:bottom-20 left-6 sm:left-20 w-56 sm:w-72 h-56 sm:h-72 bg-secondary/20 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Badge className="mb-6 bg-primary/20 text-primary-foreground border-none">
                <Globe className="h-4 w-4 mr-2" />
                Our Services
              </Badge>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-background mb-5 sm:mb-6 leading-tight"
            >
              Social Media Marketing That Helps Your Brand Show Up and Build
              Trust
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg sm:text-xl text-background/70 mb-8 max-w-2xl mx-auto leading-relaxed"
            >
              We create social media strategies, content direction, and paid
              social support that help businesses stay visible, build
              credibility, and connect with the right audience more
              consistently.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-center justify-center"
            >
              <Button
                variant="gradient"
                size="lg"
                asChild
                className="group w-full sm:w-auto justify-center"
              >
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2"
                >
                  Book a Free Consultation
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>

              <Button
                variant="glass"
                size="lg"
                asChild
                className="w-full sm:w-auto justify-center text-white bg-white/10 border border-white/20 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] hover:-translate-y-[1px] transition-all duration-300"
              >
                <Link to="/contact">View Our Work</Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Value Section */}
      <section className="pt-12 sm:pt-14 lg:pt-20 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 right-10 w-72 h-72 bg-secondary/5 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-5xl mx-auto text-center"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4">
              Visibility Builds Familiarity. Familiarity Builds{" "}
              <span className="gradient-text">Trust.</span>
            </h2>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto mb-6">
              For many businesses, social media shapes the first impression long
              before a call, form submission, or sale. Consistent, useful
              content helps people understand your brand, recognize your value,
              and remember you when they are ready to take action.
            </p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-card/60 backdrop-blur rounded-2xl border border-border shadow-soft p-6 sm:p-8"
            >
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto mb-6">
                Good social media marketing is not just posting for the sake of
                posting. It is about showing up clearly, speaking to the right
                audience, and supporting the wider goals of the business.
              </p>

              <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
                {[
                  {
                    title: "Consistent visibility",
                    desc: "Stay visible to your audience instead of disappearing between campaigns.",
                  },
                  {
                    title: "Stronger brand trust",
                    desc: "Clear and useful content helps people feel more confident in your business.",
                  },
                  {
                    title: "Better message control",
                    desc: "A social strategy helps you shape how your brand is seen online.",
                  },
                  {
                    title: "Support for other channels",
                    desc: "Social media works best when it supports your website, SEO, and wider marketing efforts.",
                  },
                  {
                    title: "Paid social opportunities",
                    desc: "Campaigns can help extend your reach when the message and offer are clear.",
                  },
                ].map((item, i) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: 0.15 + i * 0.05 }}
                    className="rounded-xl border border-border bg-background/70 p-4 text-center hover:shadow-medium transition-shadow"
                  >
                    <div className="text-sm font-semibold text-foreground">
                      {item.title}
                    </div>
                    <div className="mt-1 text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      {item.title === "Support for other channels" ? (
                        <>
                          Social media works best when it supports your{" "}
                          <Link
                            to="/services/website-development"
                            className="text-primary hover:underline"
                          >
                            website
                          </Link>
                          ,{" "}
                          <Link
                            to="/services/seo-services"
                            className="text-primary hover:underline"
                          >
                            SEO
                          </Link>
                          , and wider marketing efforts.
                        </>
                      ) : (
                        item.desc
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="pt-12 sm:pt-14 lg:pt-14 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-10"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground">
              Why Choose Square Trigger for Social Media Marketing
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground mt-4 leading-relaxed">
              We do not treat social media like a content calendar alone. We
              look at what the business needs to communicate, who it needs to
              reach, and how the content should support trust, visibility, and
              growth.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="p-6 bg-card rounded-2xl border border-border shadow-soft hover:shadow-medium transition-shadow text-center sm:text-left flex flex-col items-center sm:items-start"
              >
                <div className="w-10 h-10 rounded-xl gradient-primary-soft flex items-center justify-center mb-4">
                  <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                </div>

                <h3 className="font-semibold text-foreground mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-12 sm:mb-14"
          >
            <span className="text-sm font-medium text-primary uppercase tracking-wider">
              Capabilities
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mt-3">
              What Our Social Media Marketing Service{" "}
              <span className="gradient-text">Includes</span>
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {capabilities.map((capability, index) => {
              const Icon = capability.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.07 }}
                  className="h-full"
                >
                  <Card className="p-6 h-full border-border hover:border-primary/30 transition-all duration-300 card-hover text-center sm:text-left flex flex-col items-center sm:items-start">
                    <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-primary-foreground" />
                    </div>

                    <h3 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-2">
                      {capability.title}
                    </h3>
                    <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                      {capability.title === "Content Creation Support" ? (
                        <>
                          Support for branded post copy, creative direction, and
                          campaign-ready content planning.
                        </>
                      ) : capability.title === "Community Presence" ? (
                        <>
                          Support for building a more active and credible{" "}
                          <Link
                            to="/services/website-development"
                            className="text-primary hover:underline"
                          >
                            brand presence online
                          </Link>
                          .
                        </>
                      ) : (
                        capability.description
                      )}
                    </p>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="section-padding bg-foreground text-background relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-10 w-80 h-80 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-secondary/10 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-12 sm:mb-16"
          >
            <span className="text-sm font-medium text-secondary uppercase tracking-wider">
              Solutions
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold mt-3">
              Social Media Support Built Around{" "}
              <span className="gradient-text">Real Business Goals</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {offerings.map((offering, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="bg-background/5 rounded-2xl p-6 sm:p-8 border border-background/10 hover:border-primary/30 transition-colors text-center sm:text-left flex flex-col items-center sm:items-start"
              >
                <h3 className="text-xl sm:text-2xl font-display font-bold mb-3">
                  {offering.title === "Business-Focused Social Direction" ? (
                    <Link
                      to="/services/website-development"
                      className="hover:underline"
                    >
                      {offering.title}
                    </Link>
                  ) : (
                    offering.title
                  )}
                </h3>

                <p className="text-background/70 leading-relaxed">
                  {offering.title === "Business-Focused Social Direction" ? (
                    <>
                      Social media support that works alongside your{" "}
                      <Link
                        to="/services/website-development"
                        className="text-primary hover:underline"
                      >
                        website
                      </Link>
                      ,{" "}
                      <Link
                        to="/services/seo-services"
                        className="text-primary hover:underline"
                      >
                        SEO
                      </Link>
                      , and broader digital presence.
                    </>
                  ) : (
                    offering.description
                  )}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Deliverables */}
      <section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="text-center lg:text-left"
            >
              <span className="text-sm font-medium text-primary uppercase tracking-wider">
                Deliverables
              </span>

              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mt-3 mb-5">
                What You&apos;ll <span className="gradient-text">Receive</span>
              </h2>

              <p className="text-base sm:text-lg text-muted-foreground mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0">
                Our social media marketing service is scoped around what your
                brand needs to communicate more clearly and more consistently.
              </p>

              <Button
                variant="gradient"
                size="lg"
                asChild
                className="group w-full sm:w-auto justify-center"
              >
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2"
                >
                  Book a Free Consultation
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="bg-card rounded-2xl p-6 sm:p-8 border border-border shadow-soft text-center sm:text-left"
            >
              <ul className="inline-grid sm:grid space-y-4 text-left">
                {deliverables.map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: 14 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-start gap-3 w-fit"
                  >
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-[2px]" />
                    <span className="text-sm sm:text-base text-foreground/90 leading-relaxed">
                      {item === "Brand messaging guidance for social posts" ? (
                        <>
                          <Link
                            to="/services/website-development"
                            className="text-primary hover:underline"
                          >
                            Brand messaging
                          </Link>{" "}
                          guidance for social posts
                        </>
                      ) : (
                        item
                      )}
                    </span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="pt-12 sm:pt-14 lg:pt-12 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-12 sm:mb-16"
          >
            <span className="text-sm font-medium text-primary uppercase tracking-wider">
              Our Process
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mt-3">
              How We Approach{" "}
              <span className="gradient-text">Social Media Marketing</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="relative h-full"
              >
                <Card className="p-6 h-full border-border hover:border-primary/30 transition-colors card-hover text-center sm:text-left flex flex-col items-center sm:items-start">
                  <div className="w-full mb-4 flex justify-center">
                    <div className="text-3xl font-display font-bold gradient-text text-center">
                      {step.step}
                    </div>
                  </div>

                  <h3 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    {step.title === "Content Direction" ? (
                      <>
                        We shape what should be posted, how it should sound, and
                        how it should support trust and visibility.
                      </>
                    ) : (
                      step.description
                    )}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mid CTA */}
      <section className="py-16 sm:py-20 gradient-primary relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-80 h-80 sm:w-96 sm:h-96 bg-background/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-80 h-80 sm:w-96 sm:h-96 bg-background/10 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-primary-foreground mb-4 sm:mb-6">
              Need Social Media That Supports the Business, Not Just the Feed?
            </h2>

            <p className="text-base sm:text-lg text-primary-foreground/80 mb-8 max-w-2xl mx-auto leading-relaxed">
              Let&apos;s talk about what your audience needs to see, what your
              brand needs to say, and how social media should support real
              growth.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
              <Button
                variant="premium"
                size="lg"
                asChild
                className="bg-background text-foreground hover:bg-background/90 w-full sm:w-auto justify-center"
              >
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2"
                >
                  Book a Free Consultation
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </Button>

              <Button
                variant="glass"
                size="lg"
                asChild
                className="w-full sm:w-auto justify-center text-white bg-white/10 border border-white/20 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] hover:-translate-y-[1px] transition-all duration-300"
              >
                <Link to="/contact">Contact Us</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQs */}
      <section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-10 sm:mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mt-3">
              Social Media Marketing –{" "}
              <span className="gradient-text">Frequently Asked Questions</span>
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={faq.question}
                className="relative z-10 bg-card rounded-xl border border-border overflow-hidden"
              >
                <button
                  type="button"
                  onPointerUp={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 sm:p-6 text-left hover:bg-accent/50 transition-colors pointer-events-auto touch-manipulation"
                >
                  <span className="font-semibold text-foreground pr-6">
                    {faq.question}
                  </span>
                  <ChevronDown
                    className={`h-5 w-5 text-muted-foreground transition-transform duration-300 flex-shrink-0 ${
                      openFaq === index ? "rotate-180" : ""
                    }`}
                  />
                </button>

                <div
                  className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                    openFaq === index ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="px-5 sm:px-6 pb-5 sm:pb-6 text-muted-foreground leading-relaxed">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: faqs.map((faq) => ({
                "@type": "Question",
                name: faq.question,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: faq.schemaAnswer,
                },
              })),
            })}
          </script>
        </div>
      </section>

      {/* Final CTA */}
      <section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4 sm:mb-6">
              Ready to Build a Stronger Social Media Presence?
            </h2>

            <p className="text-base sm:text-lg text-muted-foreground mb-8 leading-relaxed max-w-2xl mx-auto">
              Let&apos;s create a social media direction that helps your
              business stay visible, communicate clearly, and build stronger
              trust online.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
              <Button
                variant="gradient"
                size="lg"
                asChild
                className="group w-full sm:w-auto justify-center"
              >
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2"
                >
                  Book a Free Consultation
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>

              <Button
                variant="outline"
                size="lg"
                asChild
                className="w-full sm:w-auto justify-center"
              >
                <Link
                  to="/contact"
                  className="flex items-center justify-center"
                >
                  Contact Us
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}